import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Heart, Calculator, ShoppingBag, Brain, GraduationCap, Dumbbell,
  Scale, Plane, PenTool, Wrench, Sparkles, ArrowRight, ArrowLeft,
  Check, X, Loader2, ChevronRight, Settings, Star, AlertTriangle,
  Cpu, Zap, MessageSquare, User, Mic, Edit2
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import api from "../lib/api";
import { FREE_MODELS, MODEL_COLORS, fetchModelCatalog } from "../lib/aiModels";
import { toast } from "sonner";

const ICON_MAP = {
  heart: Heart, calculator: Calculator, "shopping-bag": ShoppingBag,
  brain: Brain, "graduation-cap": GraduationCap, dumbbell: Dumbbell,
  scale: Scale, plane: Plane, "pen-tool": PenTool, wrench: Wrench,
};

const TONES = [
  { id: "friendly", label: "Friendly", desc: "Warm, approachable, and supportive", icon: Heart },
  { id: "professional", label: "Professional", desc: "Formal, structured, and precise", icon: Scale },
  { id: "casual", label: "Casual", desc: "Relaxed, fun, and conversational", icon: MessageSquare },
  { id: "direct", label: "Direct", desc: "Concise, no-nonsense, and efficient", icon: Zap },
];

const VOICES = [
  { id: "male", label: "Male", icon: User },
  { id: "female", label: "Female", icon: User },
  { id: "neutral", label: "Neutral", icon: Mic },
];

const PURPOSE_GRADIENTS = {
  best_friend: "from-pink-500 to-rose-600",
  accountant: "from-blue-500 to-sky-600",
  personal_shopper: "from-rose-500 to-orange-500",
  therapist: "from-violet-500 to-violet-500",
  tutor: "from-emerald-500 to-teal-600",
  fitness_coach: "from-orange-500 to-amber-600",
  legal_advisor: "from-slate-600 to-gray-700",
  travel_planner: "from-teal-500 to-cyan-600",
  writing_partner: "from-purple-500 to-violet-600",
  tech_support: "from-gray-500 to-slate-600",
  custom: "from-cyan-400 to-violet-500",
};

const PLAN_GRADIENTS = {
  basic: "from-gray-500 to-slate-600",
  standard: "from-blue-500 to-indigo-600",
  premium: "from-violet-500 to-violet-500",
  enterprise: "from-amber-500 to-orange-600",
};

const STEP_LABELS = ["Purpose", "Voice", "Personality", "Name & URL", "AI Model", "Plan"];

function generateSlug(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30);
}

function planPriceLabel(plan) {
  if (!plan) return "";
  if (plan.price === 0) return plan.price_label || "Free";
  return plan.price_label || `$${plan.price}/mo`;
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [models, setModels] = useState(FREE_MODELS);
  const [modelsConfigured, setModelsConfigured] = useState(true);
  const [modelNotice, setModelNotice] = useState(null);

  // Wizard state
  const [purpose, setPurpose] = useState("");
  const [customPurpose, setCustomPurpose] = useState("");
  const [aiName, setAiName] = useState("");
  const [tone, setTone] = useState("friendly");
  const [voiceGender, setVoiceGender] = useState("neutral");
  const [aiModel, setAiModel] = useState("");
  const [tier, setTier] = useState("");
  const [slug, setSlug] = useState("");
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [slugSuggestions, setSlugSuggestions] = useState([]);
  const [slugChecking, setSlugChecking] = useState(false);
  const [suggestedModels, setSuggestedModels] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/templates"), api.get("/pricing")])
      .then(([t, p]) => { setTemplates(t.data); setPricing(p.data); })
      .catch(() => setLoadError(true));
    // Model catalog: fetchModelCatalog falls back to the static free list on error.
    fetchModelCatalog(api).then((catalog) => {
      setModels(catalog.models);
      setModelsConfigured(catalog.configured);
      setModelNotice(catalog.notice);
    });
  }, []);

  // Slug availability check with debounce
  useEffect(() => {
    if (!slug || slug.length < 3) { setSlugAvailable(null); return; }
    setSlugChecking(true);
    const timeout = setTimeout(() => {
      api.get(`/assistants/check-slug/${slug}`)
        .then((r) => {
          setSlugAvailable(r.data.available);
          setSlugSuggestions(r.data.suggestions || []);
        })
        .catch(() => setSlugAvailable(null))
        .finally(() => setSlugChecking(false));
    }, 500);
    return () => clearTimeout(timeout);
  }, [slug]);

  const selectTemplate = (t) => {
    setPurpose(t.purpose);
    setTone(t.default_tone);
    setVoiceGender(t.default_voice_gender);
    setSuggestedModels(t.suggested_models || []);
    if (!aiName) setAiName(t.name);
  };

  const selectedModelObj = models.find((m) => m.id === aiModel);

  const canNext = () => {
    if (step === 1) return purpose || customPurpose.trim();
    if (step === 2) return voiceGender;
    if (step === 3) return tone;
    if (step === 4) return aiName.trim().length >= 2 && slug.length >= 3 && slugAvailable === true;
    if (step === 5) return aiModel;
    if (step === 6) return tier;
    return true;
  };

  const handleCreate = async () => {
    setError("");
    setCreating(true);
    try {
      await api.post("/assistants", {
        slug,
        name: aiName,
        purpose: purpose || "custom",
        personality_tone: tone,
        voice_gender: voiceGender,
        ai_model: aiModel,
        subscription_tier: tier,
        custom_instructions: customPurpose || null,
      });
      setShowSuccess(true);
      toast.success("Your AI companion has been created!");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to create assistant. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  // Auto-generate slug when name changes (only on step 5)
  const autoSlug = useCallback(() => {
    if (aiName && !slug) setSlug(generateSlug(aiName));
  }, [aiName, slug]);

  useEffect(() => {
    if (step === 4) autoSlug();
  }, [step, autoSlug]);

  return (
    <div className="min-h-screen text-foreground pt-24 pb-16 px-6" data-testid="onboarding-page">
      <div className="max-w-4xl mx-auto">
        {/* Progress */}
        <div className="mb-10" data-testid="progress-indicator">
          <div className="flex items-center justify-between mb-3">
            {STEP_LABELS.map((label, i) => (
              <div key={i} className="flex items-center flex-1">
                <button
                  onClick={() => { if (i + 1 < step) setStep(i + 1); }}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border transition-all cursor-pointer ${
                    i + 1 < step
                      ? "bg-indigo-500 border-cyan-400 text-white"
                      : i + 1 === step
                      ? "bg-cyan-500/15 border-cyan-400 text-cyan-400"
                      : "bg-card border-border text-muted-foreground/70"
                  }`}
                  data-testid={`step-dot-${i + 1}`}
                >
                  {i + 1 < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </button>
                {i < 5 && (
                  <div className={`flex-1 h-px mx-2 ${i + 1 < step ? "bg-indigo-500" : "bg-muted/70"}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            {STEP_LABELS.map((label, i) => (
              <span key={i} className={`text-[10px] ${i + 1 === step ? "text-cyan-400" : "text-muted-foreground/70"} flex-1 text-center`}>
                {label}
              </span>
            ))}
          </div>
          <div className="w-full bg-muted/70 rounded-full h-1 mt-3 overflow-hidden">
            <div className="bg-gradient-to-r from-cyan-400 to-violet-500 h-1 rounded-full transition-all duration-500 ease-out" style={{ width: `${((step - 1) / 5) * 100}%` }} data-testid="progress-bar" />
          </div>
        </div>

        {/* Step Content */}
        <div key={step} className="animate-fade-in-up">
          {loadError && (
            <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-500" data-testid="onboarding-load-error">
              Failed to load configuration. Please refresh the page.
            </div>
          )}
          {/* ─── Step 1: Purpose ─── */}
          {step === 1 && (
            <div data-testid="step-1-purpose">
              <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-light mb-2">
                Choose your AI <span className="text-cyan-400">companion&apos;s</span> purpose
              </h2>
              <p className="text-sm text-muted-foreground mb-8">Choose a template or create a custom companion.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {templates.map((t) => {
                  const Icon = ICON_MAP[t.icon_name] || Sparkles;
                  const selected = purpose === t.purpose;
                  const gradient = PURPOSE_GRADIENTS[t.purpose] || PURPOSE_GRADIENTS.custom;
                  return (
                    <button
                      key={t.purpose}
                      onClick={() => selectTemplate(t)}
                      className={`relative text-left rounded-xl overflow-hidden transition-all duration-200 cursor-pointer card-hover ${
                        selected
                          ? "shadow-lg ring-2 ring-white/50 scale-[1.02]"
                          : "hover:scale-[1.01]"
                      }`}
                      data-testid={`template-select-${t.purpose}`}
                    >
                      <div className={`bg-gradient-to-br ${gradient} p-4`}>
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/20">
                            <Icon className="w-4.5 h-4.5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-white">{t.name}</div>
                            <div className="text-xs text-white/70 mt-0.5 line-clamp-2">{t.description}</div>
                          </div>
                        </div>
                        {selected && <Check className="absolute top-3 right-3 w-4 h-4 text-white" />}
                      </div>
                    </button>
                  );
                })}
                {/* Custom option */}
                <button
                  onClick={() => { setPurpose("custom"); setSuggestedModels([]); }}
                  className={`text-left rounded-xl overflow-hidden transition-all duration-200 cursor-pointer card-hover ${
                    purpose === "custom"
                      ? "shadow-lg ring-2 ring-white/50 scale-[1.02]"
                      : "hover:scale-[1.01]"
                  }`}
                  data-testid="template-select-custom"
                >
                  <div className={`bg-gradient-to-br ${PURPOSE_GRADIENTS.custom} p-4`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/20">
                        <Settings className="w-4.5 h-4.5 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">Custom</div>
                        <div className="text-xs text-white/70 mt-0.5">Define your own purpose</div>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
              {purpose === "custom" && (
                <div className="mt-4">
                  <Input
                    data-testid="custom-purpose-input"
                    placeholder="Describe what your AI should do..."
                    value={customPurpose}
                    onChange={(e) => setCustomPurpose(e.target.value)}
                    className="bg-muted/40 border-border text-foreground placeholder:text-muted-foreground/70 h-11 focus:border-cyan-400"
                  />
                </div>
              )}
            </div>
          )}

          {/* ─── Step 2: Voice Gender ─── */}
          {step === 2 && (
            <div data-testid="step-2-voice">
              <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-light mb-2">
                Choose your AI&apos;s <span className="text-cyan-400">voice</span>
              </h2>
              <p className="text-sm text-muted-foreground mb-8">How should your AI companion sound?</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
                {[
                  { id: "male", label: "Male", desc: "Deep, confident tone", gradient: "from-blue-500 to-indigo-600", emoji: "🎙️" },
                  { id: "female", label: "Female", desc: "Warm, clear tone", gradient: "from-pink-500 to-rose-600", emoji: "🎤" },
                  { id: "neutral", label: "Neutral", desc: "Balanced, natural tone", gradient: "from-violet-500 to-purple-600", emoji: "🔊" },
                ].map((v) => {
                  const selected = voiceGender === v.id;
                  return (
                    <button
                      key={v.id}
                      onClick={() => setVoiceGender(v.id)}
                      className={`relative text-left rounded-xl overflow-hidden transition-all duration-200 cursor-pointer card-hover ${
                        selected ? "shadow-lg ring-2 ring-white/50 scale-[1.02]" : "hover:scale-[1.01]"
                      }`}
                      data-testid={`voice-select-${v.id}`}
                    >
                      <div className={`bg-gradient-to-br ${v.gradient} p-6 text-center`}>
                        <div className="text-3xl mb-3">{v.emoji}</div>
                        <div className="text-base font-medium text-white mb-1">{v.label}</div>
                        <div className="text-xs text-white/70">{v.desc}</div>
                        {selected && <Check className="absolute top-3 right-3 w-4 h-4 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Voice Preview */}
              <div className="mt-6 p-4 bg-gradient-to-r from-cyan-500/10 to-violet-500/10 rounded-xl border border-cyan-500/20 max-w-2xl" data-testid="voice-preview-section">
                <div className="flex items-center gap-2 mb-3">
                  <Mic className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm font-medium text-foreground/80">Preview Voice</span>
                </div>
                <div className="flex gap-3">
                  {VOICES.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => {
                        if ('speechSynthesis' in window) {
                          speechSynthesis.cancel();
                          const utter = new SpeechSynthesisUtterance(`Hello! I'm your AI companion. How can I help you today?`);
                          const voices = speechSynthesis.getVoices();
                          const target = voices.find(voice => v.id === 'male' ? /male/i.test(voice.name) && !/female/i.test(voice.name) : v.id === 'female' ? /female/i.test(voice.name) : true);
                          if (target) utter.voice = target;
                          speechSynthesis.speak(utter);
                        }
                      }}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm border cursor-pointer transition-all ${
                        voiceGender === v.id ? "bg-cyan-500/20 border-cyan-500/40 text-cyan-300" : "bg-card border-border text-muted-foreground hover:border-cyan-500/30"
                      }`}
                      data-testid={`voice-preview-${v.id}`}
                    >
                      <span>▶</span> {v.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── Step 3: Personality ─── */}
          {step === 3 && (
            <div data-testid="step-3-personality">
              <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-light mb-2">
                Set the <span className="text-cyan-400">personality</span> tone
              </h2>
              <p className="text-sm text-muted-foreground mb-8">How should your AI communicate?</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                {[
                  { id: "professional", label: "Professional", desc: "Formal, structured, and precise. Great for business contexts.", icon: Scale, gradient: "from-slate-500 to-gray-700" },
                  { id: "friendly", label: "Casual & Friendly", desc: "Warm, approachable, and conversational. Like talking to a friend.", icon: Heart, gradient: "from-pink-500 to-rose-600" },
                  { id: "casual", label: "Witty & Humorous", desc: "Fun, playful, with clever observations and lighthearted banter.", icon: MessageSquare, gradient: "from-amber-500 to-orange-600" },
                  { id: "empathetic", label: "Empathetic & Supportive", desc: "Warm, understanding, validates feelings. Ideal for wellness.", icon: Brain, gradient: "from-violet-500 to-purple-600" },
                  { id: "direct", label: "Direct & No-nonsense", desc: "Concise, efficient, gets straight to the point. Zero fluff.", icon: Zap, gradient: "from-cyan-500 to-blue-600" },
                ].map((t) => {
                  const selected = tone === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTone(t.id)}
                      className={`relative text-left rounded-xl overflow-hidden transition-all duration-200 cursor-pointer card-hover ${
                        selected ? "shadow-lg ring-2 ring-white/50 scale-[1.02]" : "hover:scale-[1.01]"
                      }`}
                      data-testid={`tone-select-${t.id}`}
                    >
                      <div className={`bg-gradient-to-br ${t.gradient} p-4`}>
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/20">
                            <t.icon className="w-4.5 h-4.5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-white">{t.label}</div>
                            <div className="text-xs text-white/70 mt-0.5">{t.desc}</div>
                          </div>
                        </div>
                        {selected && <Check className="absolute top-3 right-3 w-4 h-4 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Step 4: Name & URL ─── */}
          {step === 4 && (
            <div data-testid="step-4-name">
              <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-light mb-2">
                Name your AI <span className="text-cyan-400">companion</span>
              </h2>
              <p className="text-sm text-muted-foreground mb-8">Give it a name and claim your custom URL.</p>

              <div className="max-w-lg space-y-6">
                {/* Name Input */}
                <div>
                  <label className="text-sm text-muted-foreground font-medium mb-2 block">AI Name</label>
                  <Input
                    data-testid="ai-name-input"
                    placeholder="e.g., Luna, Atlas, Sage..."
                    value={aiName}
                    onChange={(e) => { setAiName(e.target.value); if (e.target.value && !slug) setSlug(generateSlug(e.target.value)); }}
                    className="bg-muted/40 border-border text-foreground placeholder:text-muted-foreground/70 h-12 text-base focus:border-cyan-400"
                  />
                </div>

                {/* URL */}
                <div>
                  <label className="text-sm text-muted-foreground font-medium mb-2 block">Custom URL</label>
                  <div className="flex items-center gap-0 bg-muted/40 border border-border rounded-lg overflow-hidden focus-within:border-cyan-400">
                    <span className="pl-4 text-sm text-muted-foreground whitespace-nowrap flex-shrink-0">app-mi.ai/</span>
                    <Input
                      data-testid="slug-input"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 30))}
                      placeholder="your-ai-name"
                      className="border-0 bg-transparent text-foreground placeholder:text-muted-foreground/70 h-11 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none"
                    />
                    <div className="pr-4 flex-shrink-0">
                      {slugChecking && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
                      {!slugChecking && slugAvailable === true && <Check className="w-4 h-4 text-emerald-400" />}
                      {!slugChecking && slugAvailable === false && <X className="w-4 h-4 text-red-500" />}
                    </div>
                  </div>
                  {slugAvailable === true && slug.length >= 3 && (
                    <p className="text-xs text-emerald-400 mt-2" data-testid="slug-available-msg">
                      <Check className="w-3 h-3 inline mr-1" /> app-mi.ai/{slug} is available!
                    </p>
                  )}
                  {slugAvailable === false && (
                    <div className="mt-2" data-testid="slug-taken-msg">
                      <p className="text-xs text-red-500 mb-1"><X className="w-3 h-3 inline mr-1" /> This URL is taken.</p>
                      {slugSuggestions.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs text-muted-foreground">Try:</span>
                          {slugSuggestions.map((s) => (
                            <button key={s} onClick={() => setSlug(s)} className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded-md border-0 cursor-pointer" data-testid={`slug-suggestion-${s}`}>{s}</button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-[11px] text-muted-foreground/70 mt-2">Lowercase letters, numbers, and hyphens only. 3-30 characters.</p>
                </div>
              </div>
            </div>
          )}

          {/* ─── Step 5: AI Model ─── */}
          {step === 5 && (
            <div data-testid="step-3-model">
              <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-light mb-2">
                Choose the <span className="text-cyan-400">brain</span> behind your AI
              </h2>
              <p className="text-sm text-muted-foreground mb-8">Pick from 8 free AI models — every model is included on every plan.</p>
              {!modelsConfigured && modelNotice && (
                <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 flex items-start gap-3" data-testid="models-unconfigured-notice">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-300 leading-relaxed">{modelNotice}</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {models.map((m) => {
                  const selected = aiModel === m.id;
                  const recommended = suggestedModels.includes(m.id);
                  const gradient = MODEL_COLORS[m.id] || "from-gray-500 to-slate-600";
                  return (
                    <button
                      key={m.id}
                      onClick={() => setAiModel(m.id)}
                      className={`relative text-left rounded-xl overflow-hidden transition-all duration-200 cursor-pointer card-hover ${
                        selected ? "shadow-lg ring-2 ring-white/50 scale-[1.02]" : "hover:scale-[1.01]"
                      }`}
                      data-testid={`model-select-${m.id}`}
                    >
                      {recommended && (
                        <span className="absolute -top-2 right-3 z-10 text-[9px] font-semibold uppercase tracking-wider bg-card text-cyan-400 px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm">
                          <Star className="w-2.5 h-2.5" /> Recommended
                        </span>
                      )}
                      <div className={`bg-gradient-to-br ${gradient} p-4`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <Cpu className="w-4 h-4 text-white/80" />
                          <span className="text-sm font-medium text-white">{m.name}</span>
                        </div>
                        <div className="text-[11px] text-white/60 mb-2">{m.provider}</div>
                        <div className="text-xs text-white/70 mb-2 line-clamp-1">{m.desc}</div>
                        <span className="inline-flex text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-semibold">
                          Free
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Step 6: Plan + Create ─── */}
          {step === 6 && (
            <div data-testid="step-6-plan">
              <h2 className="font-outfit text-2xl sm:text-3xl tracking-tight font-light mb-2">
                Choose your <span className="text-cyan-400">plan</span>
              </h2>
              <p className="text-sm text-muted-foreground mb-8">All 8 AI models are free on every plan — pick a tier based on message limits and assistant slots.</p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {pricing.map((p) => {
                  const selected = tier === p.tier;
                  const gradient = PLAN_GRADIENTS[p.tier] || "from-gray-500 to-slate-600";
                  return (
                    <button
                      key={p.tier}
                      onClick={() => setTier(p.tier)}
                      className={`relative text-left rounded-xl overflow-hidden transition-all duration-200 cursor-pointer ${
                        selected ? "shadow-lg ring-2 ring-white/50 scale-[1.02]" : "hover:scale-[1.01]"
                      }`}
                      data-testid={`plan-select-${p.tier}`}
                    >
                      {p.price === 0 && (
                        <span className="absolute -top-2 right-3 z-10 text-[9px] font-semibold uppercase tracking-wider bg-card text-emerald-400 px-2 py-0.5 rounded-full shadow-sm">
                          Free
                        </span>
                      )}
                      <div className={`bg-gradient-to-br ${gradient} p-5`}>
                        <h3 className="font-outfit text-base font-medium mb-1 text-white">{p.name}</h3>
                        <div className="flex items-baseline gap-0.5 mb-1">
                          {p.price === 0 ? (
                            <span className="font-outfit text-2xl font-semibold text-white">{p.price_label || "Free"}</span>
                          ) : (
                            <>
                              <span className="font-outfit text-2xl font-semibold text-white">${p.price}</span>
                              <span className="text-xs text-white/60">/mo</span>
                            </>
                          )}
                        </div>
                        {p.max_assistants != null && (
                          <p className="text-[11px] text-white/60 mb-3">
                            {p.max_assistants === -1 ? "Unlimited assistants" : `Up to ${p.max_assistants} assistant${p.max_assistants === 1 ? "" : "s"}`}
                          </p>
                        )}
                        <ul className="space-y-1.5">
                          {p.features.map((f, fi) => (
                            <li key={fi} className="flex items-center gap-2 text-xs text-white/80">
                              <ChevronRight className="w-3 h-3 text-white/50 flex-shrink-0" />{f}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-10 pt-6 border-t border-border/60">
          <Button
            data-testid="wizard-back-btn"
            onClick={() => step > 1 ? setStep(step - 1) : navigate("/dashboard")}
            variant="outline"
            className="border-border text-foreground/80 hover:bg-white/10 hover:text-white rounded-lg bg-transparent"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {step === 1 ? "Dashboard" : "Back"}
          </Button>

          {step < 6 ? (
            <Button
              data-testid="wizard-next-btn"
              onClick={() => setStep(step + 1)}
              disabled={!canNext()}
              className="bg-gradient-to-r from-blue-500 via-sky-400 to-violet-500 hover:opacity-90 text-white px-6 rounded-lg shadow-glow-sm transition-all hover:shadow-glow-sm hover:-translate-y-0.5 disabled:opacity-40 disabled:shadow-none press-down"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              data-testid="wizard-create-btn"
              onClick={handleCreate}
              disabled={creating}
              className="bg-gradient-to-r from-blue-500 via-sky-400 to-violet-500 hover:opacity-90 text-white px-6 rounded-lg shadow-glow-sm transition-all hover:shadow-glow-sm hover:-translate-y-0.5 disabled:opacity-50 press-down"
            >
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <>Create My AI Assistant <Sparkles className="w-4 h-4 ml-2" /></>}
            </Button>
          )}
        </div>
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="bg-card border border-border text-foreground max-w-md" data-testid="success-dialog">
          <DialogHeader>
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Check className="w-7 h-7 text-emerald-400" />
            </div>
            <DialogTitle className="text-center font-outfit text-xl font-medium">Assistant Created!</DialogTitle>
            <DialogDescription className="text-center text-sm text-muted-foreground mt-2">
              Your AI assistant <span className="text-cyan-400 font-medium">{aiName}</span> is ready at{" "}
              <span className="text-foreground/80">app-mi.ai/{slug}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex justify-center">
            <Button
              data-testid="success-go-to-ai-btn"
              onClick={() => navigate(`/${slug}`)}
              className="bg-gradient-to-r from-blue-500 via-sky-400 to-violet-500 hover:opacity-90 text-white px-6 rounded-lg shadow-glow-sm"
            >
              Go to My AI <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
